import { config } from './config';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
  // Only declare a JSON body when one is actually sent (bodyless POSTs otherwise
  // trip the server's JSON parser).
  if (init?.body != null && headers['Content-Type'] == null) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!res.ok) {
    let body: { error?: { message?: string; code?: string } } | undefined;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error */
    }
    // Session gone (logged out elsewhere / expired): hard-reset to the login
    // screen instead of leaving a ghost UI full of failing queries.
    if (res.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.replace('/login');
    }
    throw new ApiError(res.status, body?.error?.message ?? res.statusText, body?.error?.code);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? res.json() : res.text()) as Promise<T>;
}

export const apiGet = <T>(path: string): Promise<T> => request<T>(path);
export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
export const apiPatch = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) });
export const apiDelete = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

/** Build a querystring from a filter object (skips empty values). */
export function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
